import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import pdf from "../image/invoices.pdf";
import { ReactComponent as BitcoinIcon } from "../image/bitcoin-svgrepo-com.svg";
import BitcoinQR from "../image/Bitcoin-lightining.jpg";
import fieldImg from "../image/img6.jpg";
import PageHeader from "./ui/PageHeader";
import { External, Download } from "./ui/Icons";

const IMPACT = [
  { num: "37", label: "Diseases detected" },
  { num: "93.8%", label: "Accuracy rate" },
  { num: "7", label: "Crops covered" },
];

/**
 * `embedded` drops the fixed-navbar offset and the page masthead so this can
 * also be rendered inside the donation dialog on the detection screen.
 */
const DonatePage = ({ embedded = false }) => {
  const { t } = useTranslation();
  useEffect(() => {
    if (!embedded) window.scrollTo(0, 0);
  }, [embedded]);

  return (
    <main className="bg-agri-paper">
      {!embedded && (
        <PageHeader
          kicker="Support our mission"
          title={t("titleDonate")}
          lede={t("subtitleDonate")}
          aside={
            <p className="max-w-measure text-[0.9375rem] leading-[1.7] text-agri-ink60">
              {t("introDonate")}
            </p>
          }
        />
      )}

      <div className={embedded ? "container-x py-10" : "container-x section-tight"}>
        {/* Where the money goes, stated before the payment details */}
        <section className="grid grid-cols-1 gap-x-14 gap-y-10 lg:grid-cols-12">
          <figure className="lg:col-span-5">
            <img
              src={fieldImg}
              alt="A member of the AgriHyphen team inspecting a potato crop in the field"
              loading="lazy"
              className="aspect-[4/3] w-full object-cover"
            />
            <figcaption className="mt-4 border-t border-agri-rule pt-3 font-mono text-[0.6875rem] uppercase leading-relaxed tracking-[0.1em] text-agri-ink40">
              Field validation, Ngozi — Burundi
            </figcaption>
          </figure>

          <div className="lg:col-span-7">
            <h2 className="kicker-rule">Your support funds</h2>
            <dl className="mt-6 grid grid-cols-3 gap-x-6 border-y border-agri-rule py-7">
              {IMPACT.map((s) => (
                <div key={s.num}>
                  <dd className="figure text-[1.75rem] leading-none text-agri-ink sm:text-[2.25rem]">
                    {s.num}
                  </dd>
                  <dt className="mt-2.5 font-mono text-[0.625rem] uppercase leading-relaxed tracking-[0.1em] text-agri-ink40">
                    {s.label}
                  </dt>
                </div>
              ))}
            </dl>

            <div className="mt-8 aspect-video w-full bg-agri-forestDeep">
              <iframe
                className="h-full w-full"
                src="https://www.youtube.com/embed/KA7K8J2h48Q"
                title={t("titleVideo") || "AgriHyphen AI impact"}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            <p className="mt-4 border-t border-agri-rule pt-3 text-[0.875rem] leading-[1.7] text-agri-ink60">
              {t("descriptionVideo") ||
                "See how your donation reaches farming communities across Burundi."}
            </p>
          </div>
        </section>

        {/* Payment channels — the page's functional core, set as ruled blocks */}
        <section className="mt-16 border-t border-agri-rule pt-12">
          <h2 className="kicker-rule">How to give</h2>

          <div className="mt-8 grid grid-cols-1 gap-x-14 gap-y-10 lg:grid-cols-12">
            <div className="lg:col-span-6">
              <h3 className="font-display text-[1.375rem] font-semibold tracking-[-0.02em] text-agri-ink">
                {t("bankTitle")}
              </h3>
              <p className="mt-3 max-w-measure text-[0.9375rem] leading-[1.7] text-agri-ink80">
                {t("bankInfo")}
              </p>
              <p className="mt-5 border border-agri-rule bg-agri-card p-4 font-mono text-[0.8125rem] leading-relaxed text-agri-ink">
                {t("account")}
              </p>
            </div>

            <div className="lg:col-span-6">
              <div className="flex items-start gap-4">
                <span className="mt-1 shrink-0 text-agri-clay">
                  <BitcoinIcon style={{ width: "24px", height: "24px" }} />
                </span>
                <div>
                  <h3 className="font-display text-[1.375rem] font-semibold tracking-[-0.02em] text-agri-ink">
                    {t("bitcoinTitle")}
                  </h3>
                  <p className="mt-3 max-w-measure text-[0.9375rem] leading-[1.7] text-agri-ink80">
                    {t("bitcoinInfo")}
                  </p>
                </div>
              </div>
              <div className="mt-5 flex items-center gap-5 border border-agri-rule bg-agri-card p-4">
                <img
                  src={BitcoinQR}
                  alt="Bitcoin Lightning QR code for donations to AgriHyphen AI"
                  className="h-28 w-28 shrink-0 object-cover"
                />
                <p className="font-mono text-[0.6875rem] uppercase leading-relaxed tracking-[0.1em] text-agri-ink40">
                  Scan with any Lightning wallet
                </p>
              </div>
            </div>
          </div>

          <div className="mt-12 flex flex-col items-start gap-5 border-t border-agri-rule pt-8 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="font-display text-[1.125rem] font-semibold tracking-[-0.015em] text-agri-ink">
                Donation instructions
              </p>
              <p className="mt-1 max-w-measure text-[0.875rem] leading-[1.7] text-agri-ink60">
                Step-by-step guidance for USD and BIF transfers, as a PDF.
              </p>
            </div>
            <button
              type="button"
              onClick={() => window.open(pdf, "_blank", "noopener")}
              className="btn-outline shrink-0"
            >
              {t("instructionsDonate")}
              <Download className="h-3.5 w-3.5" />
            </button>
          </div>
        </section>

        <p className="mt-14 border-t border-agri-rule pt-8 max-w-measureWide text-[0.9375rem] leading-[1.7] text-agri-ink60">
          Every contribution, whatever the size, extends how many farms this
          reaches. AgriHyphen AI is free to use and always will be.
        </p>

        {!embedded && (
          <a
            href="https://www.hyphentech.africa/"
            target="_blank"
            rel="noopener noreferrer"
            className="link-rule mt-6 inline-flex"
          >
            More about Hyphen Tech
            <External className="h-3.5 w-3.5" />
          </a>
        )}
      </div>
    </main>
  );
};

export default DonatePage;
